import { useState, useEffect } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";

interface SqftRangeFilterProps {
  min: number;
  max: number;
  step?: number;
  initialMin: number;
  initialMax: number;
  onSqftChange: (min: number, max: number) => void;
}

const SqftRangeFilter = ({
  min = 0,
  max = 75000, // Default max sqft, can be adjusted
  step = 50, // Default step for sqft
  initialMin,
  initialMax,
  onSqftChange,
}: SqftRangeFilterProps) => {
  const [sqftRange, setSqftRange] = useState<[number, number]>([
    initialMin,
    initialMax,
  ]);

  const [inputValues, setInputValues] = useState({
    min: initialMin.toString(),
    max: initialMax.toString(),
  });

  useEffect(() => {
    setSqftRange([initialMin, initialMax]);
    setInputValues({
      min: initialMin.toString(),
      max: initialMax.toString(),
    });
  }, [initialMin, initialMax]);

  const handleSliderChange = (values: number[]) => {
    const [newMin, newMax] = values as [number, number];
    setSqftRange([newMin, newMax]);
    setInputValues({
      min: newMin.toString(),
      max: newMax.toString(),
    });
    onSqftChange(newMin, newMax);
  };

  const handleInputChange = (value: string, type: "min" | "max") => {
    let numValue = parseInt(value) || 0;
    // Ensure numValue is within the global min/max for sqft
    numValue = Math.max(min, Math.min(max, numValue));

    const newInputValues = {
      ...inputValues,
      [type]: value, // Keep the raw input string for the input field
    };
    setInputValues(newInputValues);

    // Only update and call callback if the parsed number is valid
    if (!isNaN(numValue)) {
      const newRange = [...sqftRange] as [number, number];
      if (type === "min") {
        // Ensure min input doesn't exceed current max of the range
        newRange[0] = Math.min(numValue, sqftRange[1]);
      } else {
        // Ensure max input isn't less than current min of the range
        newRange[1] = Math.max(numValue, sqftRange[0]);
      }
      setSqftRange(newRange);
      onSqftChange(newRange[0], newRange[1]);
    }
  };

  return (
    <div className="p-4 border-b border-gray-200">
      <h3 className="font-medium text-sm text-gray-500 mb-3">SQFT RANGE</h3>
      <div className="px-2">
        <Slider
          defaultValue={sqftRange}
          min={min}
          max={max}
          step={step}
          value={sqftRange}
          onValueChange={handleSliderChange}
          className="my-6"
        />

        <div className="flex justify-between items-center gap-4 mt-4">
          <div className="flex-1">
            <Input
              type="number"
              value={inputValues.min}
              onChange={(e) => handleInputChange(e.target.value, "min")}
              min={min}
              max={max} // Max for this input field should be the global max
              step={step}
              className="w-full"
              placeholder="Min Sqft"
            />
          </div>
          <div className="text-sm text-gray-400">to</div>
          <div className="flex-1">
            <Input
              type="number"
              value={inputValues.max}
              onChange={(e) => handleInputChange(e.target.value, "max")}
              min={min} // Min for this input field should be the global min
              max={max}
              step={step}
              className="w-full"
              placeholder="Max Sqft"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SqftRangeFilter;
